import { useState, useMemo, useRef, useEffect } from 'react';
import { Input, Button } from 'reactstrap';
import { FaSearch, FaTimes } from 'react-icons/fa';

interface Player {
  userId: string;
  alias: string;
}

interface SearchablePlayerSelectProps {
  players: Player[] | undefined;
  selectedPlayerId: string | null;
  onPlayerSelect: (playerId: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

const SearchablePlayerSelect = ({
  players,
  selectedPlayerId,
  onPlayerSelect,
  placeholder = 'Search for a player...',
  disabled = false,
}: SearchablePlayerSelectProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter players based on search term
  const filteredPlayers = useMemo(() => {
    if (!players) return [];
    if (!searchTerm.trim()) return players;

    const term = searchTerm.toLowerCase();
    return players.filter(
      (player) => player.alias.toLowerCase().includes(term) || player.userId.toLowerCase().includes(term),
    );
  }, [players, searchTerm]);

  // Get selected player info
  const selectedPlayer = useMemo(() => {
    if (!selectedPlayerId || !players) return null;
    return players.find((p) => p.userId === selectedPlayerId) || null;
  }, [selectedPlayerId, players]);

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  // Handle input blur with delay to allow clicking on dropdown items
  const handleInputBlur = (e: React.FocusEvent) => {
    // Don't close if clicking within the dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.relatedTarget as Node)) {
      return;
    }

    setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 100);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Only clear selection if user is actively typing (not when component updates the search term)
    if (
      selectedPlayer &&
      value !== selectedPlayer.alias &&
      !selectedPlayer.alias.toLowerCase().includes(value.toLowerCase())
    ) {
      onPlayerSelect(null);
    }
  };

  // Handle player selection
  const handlePlayerSelect = (player: Player) => {
    console.log('handlePlayerSelect called with:', player);
    console.log('About to call onPlayerSelect with ID:', player.userId);

    // Update local state first
    setSearchTerm(player.alias);
    setIsOpen(false);
    setHighlightedIndex(-1);

    // Then notify parent component
    onPlayerSelect(player.userId);

    console.log('Selection complete');
  };

  // Handle clear selection
  const handleClear = () => {
    onPlayerSelect(null);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredPlayers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredPlayers.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredPlayers.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPlayers.length) {
          handlePlayerSelect(filteredPlayers[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Update search term when selection changes externally
  useEffect(() => {
    if (selectedPlayer) {
      setSearchTerm(selectedPlayer.alias);
    } else if (!isOpen) {
      setSearchTerm('');
    }
  }, [selectedPlayer, isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="position-relative">
      <div className="d-flex align-items-center">
        <div className="position-relative flex-grow-1">
          <Input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            className="pr-5"
            autoComplete="off"
          />
          <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
            {selectedPlayerId ? (
              <Button
                color="link"
                size="sm"
                className="text-muted p-0"
                onClick={handleClear}
                disabled={disabled}
                style={{ border: 'none', background: 'none' }}
              >
                <FaTimes size={14} />
              </Button>
            ) : (
              <FaSearch className="text-muted" size={14} />
            )}
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="position-absolute w-100 bg-dark rounded shadow-lg"
          style={{
            top: '100%',
            zIndex: 1050,
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: '#343a40',
            border: '1px solid #6f42c1',
          }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          {filteredPlayers.length === 0 ? (
            <div className="text-muted px-3 py-2">
              {searchTerm.trim() ? 'No players found' : 'No players available'}
            </div>
          ) : (
            filteredPlayers.map((player, index) => (
              <div
                key={player.userId}
                className={`cursor-pointer px-3 py-2 ${
                  index === highlightedIndex ? 'bg-primary text-white' : 'text-white'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Dropdown item clicked:', player.alias);
                  handlePlayerSelect(player);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: index === highlightedIndex ? '#0d6efd' : 'transparent',
                }}
                onMouseLeave={() => index === highlightedIndex && setHighlightedIndex(-1)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-medium">{player.alias}</span>
                  <small className={index === highlightedIndex ? 'text-white-50' : 'text-white-50'}>
                    {player.userId}
                  </small>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Selected player info */}
      {selectedPlayer && !isOpen && (
        <small className="text-muted d-block mt-1">
          Selected: {selectedPlayer.alias} ({selectedPlayer.userId})
        </small>
      )}
    </div>
  );
};

export default SearchablePlayerSelect;
